<template>
  <div>
    <div class="page-head">
      <div>
        <h2 class="page-title">品牌库</h2>
        <p class="page-sub">共 {{ pagination.total }} 个合作品牌</p>
      </div>
      <div class="page-actions">
        <button
          v-if="hasPermission('brand:add')"
          class="btn btn-primary"
          @click="handleAdd"
        >新增品牌</button>
      </div>
    </div>

    <DataTable
      :columns="columns"
      :data="brandList"
      :loading="tableLoading"
      :total="pagination.total"
      :page="pagination.currentPage"
      :page-size="pagination.pageSize"
      row-key="brandCode"
      empty-text="没有匹配的品牌"
      @update:page="handleCurrentChange"
      @update:page-size="handleSizeChange"
      @refresh="getBrandList"
    >
      <template #filters>
        <div class="filter-bar">
          <div class="filter-inline">
            <label>关键词</label>
            <el-input
              v-model="searchKeyword"
              placeholder="品牌名称或编码"
              clearable
              style="width: 240px"
              @keyup.enter="handleSearch"
            />
          </div>
          <button class="btn" @click="handleReset">重置</button>
          <button class="btn btn-primary" @click="handleSearch">查询</button>
        </div>
      </template>

      <template #cell-brandName="{ row }">
        <a
          v-if="hasPermission('brand:view')"
          class="dt-link"
          @click="handleViewDetail(row.brandCode)"
        >{{ row.brandName }}</a>
        <span v-else>{{ row.brandName }}</span>
      </template>

      <template #cell-firstStoreOpenTime="{ row }">
        {{ formatDate(row.firstStoreOpenTime) || '—' }}
      </template>

      <template #ops="{ row }">
        <button class="dt-op" @click="handleViewDetail(row.brandCode)">详情</button>
      </template>
    </DataTable>

    <!-- 新增品牌 -->
    <el-dialog
      v-model="dialogVisible"
      :title="dialogTitle"
      width="560px"
      :close-on-click-modal="false"
    >
      <el-form ref="formRef" :model="formData" :rules="formRules" label-position="top">
        <el-form-item label="品牌名称" prop="brandName">
          <el-input v-model="formData.brandName" placeholder="请输入品牌名称" maxlength="100" />
        </el-form-item>
        <el-form-item label="品牌编码" prop="brandCode">
          <el-input v-model="formData.brandCode" placeholder="请输入品牌编码" maxlength="50" />
          <p class="form-hint">只能包含字母、数字和下划线，创建后不可修改</p>
        </el-form-item>
        <el-form-item label="创始人" prop="founder">
          <el-input v-model="formData.founder" placeholder="请输入创始人姓名" maxlength="50" />
        </el-form-item>
        <el-form-item label="创始人联系方式" prop="founderPhone">
          <el-input v-model="formData.founderPhone" placeholder="请输入联系方式" maxlength="50" />
        </el-form-item>
        <el-form-item label="首店开业时间" prop="firstStoreOpenTime">
          <el-date-picker
            v-model="formData.firstStoreOpenTime"
            type="date"
            placeholder="选择首店开业时间"
            value-format="YYYY-MM-DD"
            style="width: 100%"
          />
        </el-form-item>
        <el-form-item label="实际地址" prop="entDetailAddress">
          <el-input v-model="formData.entDetailAddress" placeholder="请输入实际地址" maxlength="200" />
        </el-form-item>
      </el-form>
      <template #footer>
        <button class="btn" @click="dialogVisible = false">取消</button>
        <button class="btn btn-primary" @click="handleSubmit">确定</button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import DataTable from '@/components/DataTable.vue'
import { validatePhone } from '@/utils/validator'
import assetService from '@/services/assetService'
import store from '@/store'

// 格式化日期
const formatDate = (dateString) => {
  if (!dateString) return ''
  return new Date(dateString).toLocaleDateString('zh-CN')
}

export default {
  name: 'BrandManagement',
  components: { DataTable },
  setup() {
    const router = useRouter()

    const brandList = ref([])
    const tableLoading = ref(false)
    const searchKeyword = ref('')
    const pagination = reactive({ currentPage: 1, pageSize: 20, total: 0 })

    const dialogVisible = ref(false)
    const dialogTitle = ref('新增品牌')
    const formRef = ref(null)

    const formData = reactive({
      brandName: '',
      brandCode: '',
      brandBusinessLicense: '',
      founder: '',
      founderPhone: '',
      firstStoreOpenTime: '',
      entDetailAddress: '',
      status: 1
    })

    const formRules = {
      brandName: [
        { required: true, message: '请输入品牌名称', trigger: 'blur' },
        { max: 100, message: '品牌名称不能超过100个字符', trigger: 'blur' }
      ],
      brandCode: [
        { required: true, message: '请输入品牌编码', trigger: 'blur' },
        { pattern: /^[a-zA-Z0-9_]+$/, message: '品牌编码只能包含字母、数字和下划线', trigger: 'blur' }
      ],
      founder: [{ required: true, message: '请输入创始人姓名', trigger: 'blur' }],
      founderPhone: [
        { required: true, message: '请输入创始人联系方式', trigger: 'blur' },
        { validator: validatePhone, trigger: 'blur' }
      ],
      firstStoreOpenTime: [{ required: true, message: '请选择首店开业时间', trigger: 'change' }],
      entDetailAddress: [{ required: true, message: '请输入实际地址', trigger: 'blur' }]
    }

    const columns = [
      { prop: 'brandName', label: '品牌名称', minWidth: 160 },
      { prop: 'brandCode', label: '品牌编码', width: 130 },
      { prop: 'founder', label: '创始人', width: 110 },
      { prop: 'founderPhone', label: '联系方式', minWidth: 150 },
      { prop: 'firstStoreOpenTime', label: '首店开业时间', width: 140 }
    ]

    const permissionCodes = computed(() => {
      const permissions = store.state.permissions
      return permissions ? Object.values(permissions).map(p => p.permissionCode) : []
    })

    const hasPermission = (code) => permissionCodes.value.includes(code)

    const getBrandList = async () => {
      try {
        tableLoading.value = true
        const response = await assetService.getBrandList({
          pageNum: pagination.currentPage,
          pageSize: pagination.pageSize,
          keyword: searchKeyword.value
        })
        brandList.value = response?.data?.list || []
        pagination.total = response?.data?.total || 0
      } catch (error) {
        ElMessage.error('获取品牌列表失败：' + (error?.message || ''))
      } finally {
        tableLoading.value = false
      }
    }

    const handleSearch = () => {
      pagination.currentPage = 1
      getBrandList()
    }

    const handleReset = () => {
      searchKeyword.value = ''
      handleSearch()
    }

    const handleViewDetail = (brandCode) => {
      router.push({ name: 'brandDetail', params: { brandCode } })
    }

    const handleAdd = () => {
      Object.assign(formData, {
        brandName: '', brandCode: '', brandBusinessLicense: '',
        founder: '', founderPhone: '', firstStoreOpenTime: '',
        entDetailAddress: '', status: 1
      })
      dialogTitle.value = '新增品牌'
      dialogVisible.value = true
      nextTick(() => formRef.value?.clearValidate())
    }

    // 说明：后端 BrandController 目前仅提供 createBrand，未提供更新接口，
    // 因此这里只保留新增。待后端补充更新接口后再开放编辑。
    const handleSubmit = async () => {
      if (!formRef.value) return
      try {
        await formRef.value.validate()
        const response = await assetService.createBrand(formData)
        if (response?.code === 200) {
          ElMessage.success('品牌创建成功')
          dialogVisible.value = false
          getBrandList()
        } else {
          ElMessage.error(response?.message || '创建品牌失败')
        }
      } catch (error) {
        if (error?.message) ElMessage.error(error.message)
      }
    }

    const handleCurrentChange = (page) => {
      pagination.currentPage = page
      getBrandList()
    }

    const handleSizeChange = (size) => {
      pagination.pageSize = size
      pagination.currentPage = 1
      getBrandList()
    }

    onMounted(getBrandList)

    return {
      brandList, tableLoading, searchKeyword, pagination, columns,
      dialogVisible, dialogTitle, formRef, formData, formRules,
      formatDate, hasPermission, handleSearch, handleReset, handleViewDetail,
      handleAdd, handleSubmit, handleCurrentChange, handleSizeChange, getBrandList
    }
  }
}
</script>

<style scoped>
.page-head {
  display: flex;
  align-items: flex-end;
  margin-bottom: 16px;
}

.page-title {
  margin: 0;
  font-size: 20px;
  font-weight: 500;
  color: var(--text-primary);
}

.page-sub {
  margin: 2px 0 0;
  font-size: 12px;
  color: var(--text-tertiary);
}

.page-actions {
  margin-left: auto;
  display: flex;
  gap: 8px;
}

.btn {
  padding: 6px 14px;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-control);
  background: var(--bg-card);
  color: var(--text-secondary);
  font-family: inherit;
  font-size: 13px;
  cursor: pointer;
}

.btn:hover {
  border-color: var(--brand-500);
  color: var(--brand-500);
}

.btn-primary {
  background: var(--brand-500);
  border-color: var(--brand-500);
  color: #fff;
}

.btn-primary:hover {
  background: var(--brand-600);
  border-color: var(--brand-600);
  color: #fff;
}

.filter-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.filter-inline {
  display: flex;
  align-items: center;
  gap: 6px;
}

.filter-inline label {
  font-size: 12px;
  color: var(--text-secondary);
  white-space: nowrap;
}

.dt-link {
  color: var(--brand-500);
  cursor: pointer;
  text-decoration: none;
}

.dt-link:hover {
  text-decoration: underline;
}

.form-hint {
  margin: 4px 0 0;
  font-size: 12px;
  color: var(--text-tertiary);
  line-height: 1.5;
}
</style>
